import { CopyViewModalProvider } from '@monorepo/logs/src/contexts/CopyViewModalContext';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { useLinkOnSameProject } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameProject';
import {
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { QueryResultsTable } from '@monorepo/shared/componentsV2/QueryResultsTable';
import { EditViewInfoModalProvider } from '../../../contexts/EditViewInfoModalContext';
import { useView } from '../../../hooks/useView';
import { useViewQueryResults } from '../../../hooks/useViewQueryResults';
import { ViewDetailErrorBoundary } from './ViewDetailErrorBoundary';
import { ViewDetailToolbar } from './ViewDetailToolbar';

const Container = styled(Main)`
  ${verticalLayout}
`;

const StyledQueryResultsTable = styled(QueryResultsTable)`
  ${fillHeightAndScrollable}
`;

function ViewDetailTabContents() {
  const { organizationId, pageNumber, projectId, viewId } = useParams();

  if (!organizationId || !projectId || !viewId) {
    throw new Error(
      'ViewDetailTabContents rendered without required parameters',
    );
  }

  const navigate = useNavigate();
  const { view, isLoading } = useView({ organizationId, projectId, viewId });
  const {
    queryResult,
    isLoading: queryIsLoading,
    isFetching: queryIsFetching,
  } = useViewQueryResults({
    organizationId,
    projectId,
    requestedPage: Number(pageNumber),
    viewId,
  });

  const viewPageLink = useLinkOnSameProject(`/logs/views/${viewId}`);

  const handlePageChange = useCallback(
    (newPage: number) => {
      navigate(`${viewPageLink}/pages/${newPage}`);
    },
    [navigate, viewPageLink],
  );

  if (isLoading) {
    return <Loading />;
  }

  if (!view) {
    throw new Error('View not found');
  }

  return (
    <EditViewInfoModalProvider>
      <CopyViewModalProvider>
        <Container>
          <ViewDetailToolbar queryResult={queryResult} />
          <StyledQueryResultsTable
            isLoading={queryIsLoading || queryIsFetching}
            onPageChange={handlePageChange}
            organizationId={organizationId}
            originId={view.originId}
            projectId={projectId}
            queryResult={queryResult}
            ariaName={view.name}
          />
        </Container>
      </CopyViewModalProvider>
    </EditViewInfoModalProvider>
  );
}

export function ViewDetailTab() {
  return (
    <ViewDetailErrorBoundary>
      <ViewDetailTabContents />
    </ViewDetailErrorBoundary>
  );
}
